import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Formik } from 'formik';
import * as Yup from "yup";
import { MdClose, MdLocalPhone } from "react-icons/md";
import { FiAtSign } from "react-icons/fi";
import ContainedButton from "components/layout/form/containedButton";
import OutlinedButton from 'components/layout/form/outlinedButton';
import TextField from 'components/layout/form/textField';
import api from 'services/api';
import { toast } from 'react-toastify';

import {
  Body,
  Container,
  Content,
  Footer,
  Header,
  Style,
  ValidationContent,
  ValidationItem,
  ValidationItemContent,
  ValidationItemLabel,
  ValidationItemValue,
  FormContainer,
  InputsContainer,
  ContentButtons,
  TextTimer,
  ButtonEdit,
  FormEditIcon,
} from "./styles";

const formatTimer = (timer) => {
  let minutes = parseInt(timer / 60, 10);
  let seconds = parseInt(timer % 60, 10);
  minutes = minutes < 10 ? "0" + minutes : minutes;
  seconds = seconds < 10 ? "0" + seconds : seconds;
  return minutes + ":" + seconds;
};

const ModalValidationContacts = ({ open, toggleModal, contacts, protocol, getContacts }) => {
  const [view, setView] = useState('initial');
  const [timer, setTimer] = useState(60);
  const [startTimer, setStartTimer] = useState(false);
  const [loading, setLoading] = useState(false);

  const email = contacts?.find(contact => contact.contactType === 'EMAIL')?.contact;
  const phone = contacts?.find(contact => contact.contactType === 'PHONE')?.contact;

  const intervalRef = useRef();
  let newTimer = 60;

  const decreaseNum = () => {
    if (newTimer === 0) {
      setStartTimer(false);
      clearInterval(intervalRef.current);
      return;
    }
    setTimer((prev) => {
      newTimer = newTimer - 1;
      return prev - 1;
    })
  };

  useEffect(() => {
    if (startTimer) {
      intervalRef.current = setInterval(decreaseNum, 1000);
    }

    return () => clearInterval(intervalRef.current);
  }, [startTimer]);

  const toggleView = (viewer) => {
    setView(viewer);
  };

  const validationSchema = () =>
    Yup.object().shape({
      email: Yup.string().email('Insira um e-mail válido'),
      phone: Yup.mixed(),
      token: Yup.string(),
    });

  const sendToken = async (type, value) => {
    const userId = localStorage.getItem("@UltragazEmpresarial:userId");
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const customerSiteId = localStorage.getItem("@UltragazEmpresarial:customerSiteId");
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };

    const bodyContact = {
      contactType: type,
      contact: value,
      document,
      siteId: customerSiteId,
    };

    try {
      const { headers: headersContact } = await api.post(`industrial/customer/v1/users/${userId}/contacts`, bodyContact, { headers });

      const contactId = headersContact?.location?.split('contacts/')[1];

      const bodyToken = {
        contactId,
        tokenType: "REGISTER",
        contact: value,
        contactType: type,
        url: null,
      };

      await api.post(`industrial/customer/v1/users/${userId}/tokens`, bodyToken, {
        headers,
      });

      toast.warn(`Enviamos um código de verificação para ${value}`);
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Ocorreu um erro interno na aplicação");
      return false;
    }
  }

  const validationToken = async (token, value) => {
    const userId = localStorage.getItem("@UltragazEmpresarial:userId");
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };

    const bodyToken = {
      token,
    };

    let success;
    try {
      await api.post(`industrial/customer/v1/users/${userId}/validations`, bodyToken, {
        headers,
      });
      await getContacts();
      success = true;
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Ocorreu um erro interno na aplicação");
      success = false;
    }

    return success;
  }

  const resetStep = () => {
    toggleView('initial');
    setStartTimer(false);
    setTimer(60);
  }

  const resendToken = async (values) => {
    setLoading(true);

    const validationType = view === 'validationEmail' ? 'EMAIL' : 'PHONE';
    const validationValue = view === 'validationEmail' ? values.email : values.phone.replace(/[^\d]/g, "");
    const result = await sendToken(validationType, validationValue);

    if (result) {
      setTimer(60);
      setStartTimer(true);
    }

    setLoading(false);
  }

  const handleSubmit = async (values) => {
    setLoading(true);
    if (view === 'informEmail') {
      const result = await sendToken('EMAIL', values.email);

      if (result) {
        setStartTimer(true);
        toggleView('validationEmail');
      }
    }

    if (view === 'informPhone') {
      const result = await sendToken('PHONE', values.phone.replace(/[^\d]/g, ""));

      if (result) {
        setStartTimer(true);
        toggleView('validationPhone');
      }
    }

    if (['validationPhone', 'validationEmail'].includes(view)) {
      const success = await validationToken(values.token, view === 'validationEmail' ? values.email : values.phone.replace(/[^\d]/g, ""));
      if (success) {
        toast.success(view === 'validationEmail' ? "E-mail verificado com sucesso" : "Celular verificado com sucesso");
        resetStep();
      }
    }
    setLoading(false);
  }

  return (
    <Style show={open} centered animation={false}>
      <Container>
        <Header>
          <div className="wrapper">
            <span>Solicitação aberta com sucesso.</span>
          </div>
          <MdClose
            size={24}
            onClick={toggleModal}
          />
        </Header>
        <Body>
          <Content>
            <p>Protocolo aberto: {protocol}</p>
            <p>Em até 5 dias úteis entraremos em contato por ligação ou e-mail.</p>

            {view === 'initial' ? (
              <>
                <p>Confirme seus contatos:</p>

                <ValidationContent>
                  {email && (
                    <ValidationItem>
                      <ValidationItemContent>
                        <ValidationItemLabel>E-mail:</ValidationItemLabel>
                        <ValidationItemValue>{email}</ValidationItemValue>
                      </ValidationItemContent>
                      <ButtonEdit onClick={() => toggleView('informEmail')}>
                        <FormEditIcon />
                        <span>Editar</span>
                      </ButtonEdit>
                    </ValidationItem>
                  )}
                  {phone && (
                    <ValidationItem>
                      <ValidationItemContent>
                        <ValidationItemLabel>Celular:</ValidationItemLabel>
                        <ValidationItemValue>{phone}</ValidationItemValue>
                      </ValidationItemContent>
                      <ButtonEdit onClick={() => toggleView('informPhone')}>
                        <FormEditIcon />
                        <span>Editar</span>
                      </ButtonEdit>
                    </ValidationItem>
                  )}
                </ValidationContent>
              </>
            ) : (
              <Formik
                initialValues={{
                  email: '',
                  phone: '',
                  token: '',
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                render={({
                  handleChange,
                  handleBlur,
                  values,
                  errors,
                  touched,
                }) => (
                  <FormContainer>
                    <InputsContainer>
                      {view === 'informPhone' && (
                        <>
                          <p>Informe o celular:</p>

                          <TextField
                            LeadingIcon={() => <MdLocalPhone size={24} />}
                            name="phone"
                            mask="(99) 9 9999-9999"
                            label="Celular com DDD *"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.phone}
                            errorCondition={errors.phone && touched.phone}
                            errorMessage={errors.phone}
                            disabled={loading}
                          />
                          <ContentButtons id="content-buttons">
                            <OutlinedButton
                              disabled={!values.phone || errors.phone || loading}
                              type="submit"
                              loading={loading}
                            >
                              Enviar token
                            </OutlinedButton>
                          </ContentButtons>
                        </>
                      )}
                      {view === 'informEmail' && (
                        <>
                          <p>Informe o e-mail:</p>

                          <TextField
                            LeadingIcon={() => <FiAtSign size={24} />}
                            type="email"
                            name="email"
                            label="Digite seu e-mail"
                            value={values.email}
                            errorCondition={errors.email && touched.email}
                            errorMessage={errors.email}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            disabled={loading}
                          />
                          <ContentButtons id="content-buttons">
                            <OutlinedButton
                              disabled={!values.email || errors.email || loading}
                              type="submit"
                              loading={loading}
                            >
                              Enviar token
                            </OutlinedButton>
                          </ContentButtons>
                        </>
                      )}
                      {['validationEmail', 'validationPhone'].includes(view) && (
                        <>
                          <p>Confirmação do token:</p>
                          <TextField
                            name="token"
                            label="Inserir código *"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.token}
                            errorCondition={errors.token && touched.token}
                            errorMessage={errors.token}
                            disabled={loading}
                          />
                          <TextTimer>Não recebi o código. {timer > 0 ? `Aguarde ${formatTimer(timer)}` : <a href="#" onClick={() => resendToken(values)}>Enviar novamente</a>}</TextTimer>
                          <ContentButtons id="content-buttons">
                            <OutlinedButton
                              disabled={!values.token || errors.token || loading}
                              type="submit"
                              loading={loading}
                            >
                              Validar código
                            </OutlinedButton>
                            {timer <= 0 && (
                              <OutlinedButton
                                onClick={resetStep}
                                disabled={loading}
                                loading={loading}
                              >
                                Trocar forma de validação
                              </OutlinedButton>
                            )}
                          </ContentButtons>
                        </>
                      )}
                    </InputsContainer>
                  </FormContainer>
                )}
              />
            )}
          </Content>
        </Body>
        {view === 'initial' && (
          <Footer>
            <ContainedButton onClick={toggleModal}>
              Ok
            </ContainedButton>
          </Footer>
        )}
      </Container>
    </Style>
  );
};

export default ModalValidationContacts;
